import { useCallback, useMemo } from 'react';

export const LOGIN_METHOD_PREFERENCE_KEY = 'loginMethodPreference';

export const LOGIN_METHOD_EMAIL = 'email-login' as const;
export const LOGIN_METHOD_OTP = 'otp' as const;

export type LoginMethod = typeof LOGIN_METHOD_EMAIL | typeof LOGIN_METHOD_OTP;

export interface LoginMethodPreference {
    getLoginMethodPreference: () => LoginMethod | null;
    setLoginMethodPreference: (method: LoginMethod) => void;
    clearLoginMethodPreference: () => void;
}

/**
 * Hook to manage user's login method preference (email-login vs OTP)
 * Stores preference in localStorage to persist across sessions
 */
const useLoginMethodPreference = (): LoginMethodPreference => {
    const getLoginMethodPreference = useCallback((): LoginMethod | null => {
        const stored = localStorage.getItem(LOGIN_METHOD_PREFERENCE_KEY);
        if (stored === LOGIN_METHOD_EMAIL || stored === LOGIN_METHOD_OTP) {
            return stored;
        }
        return null;
    }, []);

    const setLoginMethodPreference = useCallback((method: LoginMethod): void => {
        localStorage.setItem(LOGIN_METHOD_PREFERENCE_KEY, method);
    }, []);

    const clearLoginMethodPreference = useCallback((): void => {
        localStorage.removeItem(LOGIN_METHOD_PREFERENCE_KEY);
    }, []);

    return useMemo(() => ({
        getLoginMethodPreference,
        setLoginMethodPreference,
        clearLoginMethodPreference,
    }), [getLoginMethodPreference, setLoginMethodPreference, clearLoginMethodPreference]);
};

export default useLoginMethodPreference;
